// ** React Imports
import { Fragment, useState, useEffect } from "react"

// ** Custom Components
import Avatar from "@components/avatar"

// ** Third Party Components
import PerfectScrollbar from "react-perfect-scrollbar"
import { Bell, X, Check, AlertTriangle, MessageCircle } from "react-feather"
import { useTranslation } from 'react-i18next'

// ** Reactstrap Imports
import {
  Button,
  Badge,
  Input,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
  Spinner
} from "reactstrap"

import { useNavigate } from "react-router-dom"

// ** WebSocket Hook
import { useWebSocket } from "@src/hooks/useWebSocket"
import { useNotificationPermission } from "@src/hooks/useNotificationPermission"

// ** API Services
import { notificationService } from "@src/services/api"

const NotificationDropdown = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  // ** WebSocket Integration
  const { isConnected } = useWebSocket({
    autoConnect: true
  })

  // ** Notification Permission Hook
  const { isEnabled: notificationsEnabled, requestPermission } = useNotificationPermission()

  // ** Local state
  const [unreadCount, setUnreadCount] = useState(0)
  const [dropdownNotifications, setDropdownNotifications] = useState([])
  const [isLoadingDropdown, setIsLoadingDropdown] = useState(false)

  /**
   * Busca contador de notificações não lidas
   */
  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount()
      if (response.data && !response.data.error) {
        setUnreadCount(response.data.obj.unread_count)
      }
    } catch (error) {
      console.error('❌ Erro ao buscar contador:', error)
    }
  }

  // ** Solicita permissão para notificações na primeira renderização
  useEffect(() => {
    if (!notificationsEnabled) {
      requestPermission()
    }
  }, [notificationsEnabled, requestPermission])

  // ** Busca contador inicial de notificações não lidas
  useEffect(() => {
    fetchUnreadCount()
  }, [])

  // ** Escuta atualizações do contador via WebSocket
  useEffect(() => {
    let cleanup = null

    import('@src/services/websocketService').then(({ default: websocketService }) => {
      const handleUnreadCountUpdate = (count) => {
        console.log('📊 Contador atualizado via WebSocket:', count)
        setUnreadCount(count)
      }

      websocketService.on('unreadCountUpdate', handleUnreadCountUpdate)

      cleanup = () => {
        websocketService.off('unreadCountUpdate', handleUnreadCountUpdate)
      }
    })

    return () => {
      if (cleanup) cleanup()
    }
  }, [])

  /**
   * Busca notificações não lidas para o dropdown
   */
  const fetchDropdownNotifications = async () => {
    if (isLoadingDropdown) return

    setIsLoadingDropdown(true)
    try {
      console.log('📋 Buscando notificações para dropdown...')
      const response = await notificationService.getUnreadForDropdown()
      
      if (response.data && !response.data.error) {
        const notifications = response.data.obj.data || []
        console.log('📋 Notificações carregadas:', notifications.length)
        setDropdownNotifications(notifications)
      }
    } catch (error) {
      console.error('❌ Erro ao buscar notificações do dropdown:', error)
    } finally {
      setIsLoadingDropdown(false)
    }
  }

  /**
   * Manipula abertura/fechamento do dropdown
   */
  const handleDropdownToggle = (isOpen) => {
    if (isOpen && !isLoadingDropdown) {
      fetchDropdownNotifications()
    }
  }

  /**
   * Marca notificação como lida
   */
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId)
      
      // Remove da lista local
      setDropdownNotifications(prev => prev.filter(n => n.id !== notificationId))
      
      // Atualiza contador
      setUnreadCount(prev => Math.max(0, prev - 1))
      
      console.log('✅ Notificação marcada como lida:', notificationId)
    } catch (error) {
      console.error('❌ Erro ao marcar como lida:', error)
    }
  }

  /**
   * Navega para o caso relacionado
   */
  const handleNotificationClick = (notification) => {
    // Marca como lida
    handleMarkAsRead(notification.id)
    
    // Navega para o caso se existir
    if (notification.caseId) {
      navigate(`/cases/${notification.caseId}`)
    } else if (notification.path) {
      navigate(notification.path)
    }
  }

  /**
   * Função para obter ícone baseado no tipo de notificação
   */
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'case_created':
      case 'case_updated':
      case 'case_status_changed':
        return <MessageCircle size={14} />
      case 'payment_received':
        return <Check size={14} />
      case 'deadline_reminder':
      case 'system_alert':
        return <AlertTriangle size={14} />
      default:
        return <Bell size={14} />
    }
  }

  /**
   * Função para obter cor baseada no tipo de notificação
   */
  const getNotificationColor = (priority) => {
    switch (priority) {
      case 3:
        return 'light-danger' // Alta prioridade
      case 2:
        return 'light-warning' // Média prioridade
      case 1:
        return 'light-info' // Baixa prioridade
      default:
        return 'light-primary'
    }
  }

  // ** Function to render Notifications
  /*eslint-disable */
  const renderNotificationItems = () => {
    if (isLoadingDropdown) {
      return (
        <li className="d-flex justify-content-center p-2">
          <Spinner size="sm" />
        </li>
      )
    }

    if (dropdownNotifications.length === 0) {
      return (
        <li className="d-flex justify-content-center p-2">
          <small className="text-muted">{t("No unread notifications")}</small>
        </li>
      )
    }

    return (
      <PerfectScrollbar
        component="li"
        className="media-list scrollable-container"
        options={{
          wheelPropagation: false
        }}
      >
        {dropdownNotifications.map((notification) => {
          return (
            <a
              key={notification.id}
              className="d-flex"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                handleNotificationClick(notification)
              }}
            >
              <div className="list-item d-flex align-items-start">
                <div className="me-1">
                  <Avatar
                    icon={getNotificationIcon(notification.notificationType)}
                    color={getNotificationColor(notification.priority)}
                  />
                </div>
                <div className="list-item-body flex-grow-1">
                  <p className="media-heading">
                    <span className="fw-bolder">{notification.title}</span>
                  </p>
                  <small className="notification-text">
                    {notification.message}
                  </small>
                  {notification.sentAt && (
                    <small className="text-muted d-block">
                      {new Date(notification.sentAt).toLocaleString()}
                    </small>
                  )}
                </div>
              </div>
            </a>
          )
        })}
      </PerfectScrollbar>
    )
  }
  /*eslint-enable */

  return (
    <UncontrolledDropdown
      tag="li"
      className="dropdown-notification nav-item me-25"
      onToggle={handleDropdownToggle}
    >
      <DropdownToggle
        tag="a"
        className="nav-link"
        href="/"
        onClick={(e) => e.preventDefault()}
      >
        <Bell size={21} />
        {unreadCount > 0 && (
          <Badge pill color="danger" className="badge-up">
            {unreadCount}
          </Badge>
        )}
        {!isConnected && (
          <Badge pill color="warning" className="badge-up ms-1">
            !
          </Badge>
        )}
      </DropdownToggle>
      <DropdownMenu end tag="ul" className="dropdown-menu-media mt-0">
        <li className="dropdown-menu-header">
          <DropdownItem className="d-flex" tag="div" header>
            <h4 className="notification-title mb-0 me-auto">
              {t("Notifications")}
              {!isConnected && (
                <small className="text-warning ms-2">({t("Offline")})</small>
              )}
            </h4>
            <Badge tag="div" color="light-primary" pill>
              {unreadCount} {t("Unread")}
            </Badge>
          </DropdownItem>
        </li>
        {renderNotificationItems()}
        <li className="dropdown-menu-footer">
          <Button
            color="primary"
            block
            onClick={() => navigate("/notifications")}
          >
            {t("View All Notifications")}
          </Button>
        </li>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

export default NotificationDropdown
